if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      connectedCallback() {
        this.arRestoreCheckout = () => {
          if (!this.arRedirecting) return;
          this.arRedirecting = false;
          this.arPurchasePending = false;
          this.submitButton.removeAttribute('aria-disabled');
          this.submitButton.classList.remove('loading');
          this.querySelector('.loading__spinner')?.classList.add('hidden');
          const button = this.querySelector('[data-ar-buy-now]');
          if (button) {
            button.disabled = this.submitButton.disabled;
            button.removeAttribute('aria-busy');
            button.classList.remove('loading');
            button.querySelector('.loading__spinner')?.classList.add('hidden');
            button.querySelector('[data-ar-buy-now-label]').textContent = 'Buy now';
          }
        };
        window.addEventListener('pageshow', this.arRestoreCheckout);
      }

      disconnectedCallback() {
        window.removeEventListener('pageshow', this.arRestoreCheckout);
      }

      selectedPurchaseVariant() {
        let variants;
        try { variants = JSON.parse(this.querySelector('[data-ar-purchase-variants]')?.textContent || '[]'); }
        catch (_) { return null; }
        const info = this.closest('product-info');
        const appOptions = [...(info?.querySelectorAll('input[data-globoswatchindex]:checked') || [])]
          .sort((a, b) => Number(a.dataset.globoswatchindex.replace('option', '')) - Number(b.dataset.globoswatchindex.replace('option', '')))
          .map(input => input.value);
        const nativeOptions = [...(info?.querySelectorAll('variant-selects fieldset, variant-selects .product-form__input--dropdown') || [])]
          .map(group => group.querySelector('input:checked, select')?.value);
        const options = appOptions.length ? appOptions : nativeOptions;
        if (options.length) return variants.find(variant => variant.options.length === options.length && variant.options.every((option, index) => option === options[index]));
        return variants.find(variant => String(variant.id) === this.variantIdInput.value);
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.arPurchasePending || this.submitButton.disabled || this.submitButton.getAttribute('aria-disabled') === 'true') return;
        const buyNow = evt.submitter?.matches('[data-ar-buy-now]') === true;
        if (buyNow && (this.closest('quick-add-modal') || !this.closest('product-info[id^="MainProduct-"]'))) return;
        const selectedVariant = this.selectedPurchaseVariant();
        if (!selectedVariant?.available) {
          this.handleErrorMessage('This colour and size is unavailable. Please choose another option.');
          return;
        }
        const quantityInput = this.form.elements.namedItem('quantity');
        if (quantityInput && !quantityInput.reportValidity()) return;
        this.arPurchasePending = true;
        const buyNowButton = this.querySelector('[data-ar-buy-now]');
        if (buyNowButton) {
          buyNowButton.disabled = true;
          if (buyNow) {
            buyNowButton.setAttribute('aria-busy', 'true');
            buyNowButton.classList.add('loading');
            buyNowButton.querySelector('.loading__spinner')?.classList.remove('hidden');
            buyNowButton.querySelector('[data-ar-buy-now-label]').textContent = 'Opening checkout…';
          }
        }

        this.handleErrorMessage();
        this.error = false;
        if (!buyNow) window.AuraInteractions?.cartState(this.submitButton, 'loading');

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        formData.set('id', String(selectedVariant.id));
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        const variantId = formData.get('id');
        const quantity = parseInt(formData.get('quantity')) || 1;
        const linesUpdateDeferred = this.createCartLinesUpdateEvent(variantId, quantity);

        fetch(`${routes.cart_add_url}`, config)
          .then(async (response) => {
            const data = await response.json();
            if (!response.ok && !data.status) throw new Error('Cart request failed');
            return data;
          })
          .then(async (response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: variantId,
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);
              window.AuraInteractions?.cartState(this.submitButton, 'error');
              this.dispatchCartErrorEvent(response.description || response.message, 'INVALID');
              linesUpdateDeferred?.reject(new Error(response.description || response.message));

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else {
              if (!response.id && !Array.isArray(response.items)) throw new Error('Cart confirmation was missing');
              const cartLinesUpdate = this.resolveCartLinesUpdate(linesUpdateDeferred);
              if (buyNow) {
                const cartIcon = document.getElementById('cart-icon-bubble');
                const iconSection = response.sections?.['cart-icon-bubble'];
                if (cartIcon && iconSection) {
                  const updatedIcon = new DOMParser().parseFromString(iconSection, 'text/html').querySelector('.shopify-section');
                  if (updatedIcon) cartIcon.innerHTML = updatedIcon.innerHTML;
                }
                const cartSubscribers = publish(PUB_SUB_EVENTS.cartUpdate, {
                  source: 'product-form', productVariantId: variantId, cartData: response,
                });
                // Let local cart events settle without allowing a subscriber to block checkout.
                await Promise.race([
                  Promise.allSettled([cartLinesUpdate, cartSubscribers]),
                  new Promise(resolve => setTimeout(resolve, 1500)),
                ]);
                this.arRedirecting = true;
                window.location.assign((window.Shopify?.routes?.root || '/') + 'checkout');
                return;
              }
              window.AuraInteractions?.cartState(this.submitButton, 'success');
              this.submitButton.classList.remove('loading');
              this.querySelector('.loading__spinner')?.classList.add('hidden');
              try {
                await window.AuraShopping?.cartAdded(this.submitButton);
              } catch (animationError) {
                console.warn('Cart animation skipped; item was added.', animationError);
              }
            }


            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: variantId,
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart?.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart?.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
            this.handleErrorMessage('Unable to confirm this item. Check your cart before trying again.');
            window.AuraInteractions?.cartState(this.submitButton, 'error');
            this.dispatchCartErrorEvent(e.message || 'Network error', 'SERVICE_UNAVAILABLE');
            linesUpdateDeferred?.reject(e);
          })
          .finally(() => {
            if (this.arRedirecting) return;
            this.arPurchasePending = false;
            if (buyNowButton) {
              buyNowButton.disabled = this.submitButton.disabled || this.error;
              buyNowButton.removeAttribute('aria-busy');
              buyNowButton.classList.remove('loading');
              buyNowButton.querySelector('.loading__spinner')?.classList.add('hidden');
              buyNowButton.querySelector('[data-ar-buy-now-label]').textContent = 'Buy now';
            }
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        const buyNowButton = this.querySelector('[data-ar-buy-now]');
        if (buyNowButton) buyNowButton.disabled = disable || this.arPurchasePending === true;
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.error = false;
          this.submitButton.removeAttribute('disabled');
          this.submitButton.removeAttribute('aria-disabled');
          this.submitButtonText.classList.remove('hidden');
          this.submitButton.querySelector('.sold-out-message')?.classList.add('hidden');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      createCartLinesUpdateEvent(variantId, quantity) {
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return null;

        const deferred = CartLinesUpdateEvent.createPromise();
        this.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context: 'product',
            lines: [{ merchandiseId: variantId, quantity }],
            promise: deferred.promise,
          })
        );
        return deferred;
      }

      resolveCartLinesUpdate(deferred) {
        if (!deferred) return;
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return;

        const pendingCartDataPromise = typeof CartItems !== 'undefined'
          ? CartItems.fetchCartData()
          : fetch(`${routes.cart_url}.json`).then((response) => response.json());

        return pendingCartDataPromise
          .then((cart) => {
            if (!cart?.currency) return deferred.reject(new Error('Missing currency in cart response'));
            deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
          })
          .catch((e) => deferred.reject(e));
      }

      dispatchCartErrorEvent(message, code) {
        const { CartErrorEvent } = window.StandardEvents || {};
        if (!CartErrorEvent) return;
        this.dispatchEvent(new CartErrorEvent({ error: message, code }));
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
